import { useState, useEffect, useCallback } from 'react';
import { fetchCarryForwardNotes } from '../../../../services/SongNotesService';
import {
	groupCarryForwardBySong,
	mapLegacyStatus,
} from '../../../../services/songNotesModel';

/**
 * Carry-forward — this student's outstanding Song Notes from every prior lesson
 * and every teacher. Query + grouping ported from CarryForwardNotes.js (N1b).
 *
 * THIS IS THE FEATURE, not a nicety: CYV school-year students rotate through
 * all three teaching artists on one weekly slot. Without it a teacher opens a
 * lesson blind to what the previous two teachers noticed or withheld.
 * Teacher-only by definition — `captured` notes appear here in full.
 *
 * The per-lesson TUCK (the pill's one-way dismiss, `lessons.carryForwardTuckedAt`) was
 * REMOVED by design pass 2 (2026-09-21): it hid the review for the rest of the
 * lesson with no way back, and on an iPad Warm-ups tab it was the only
 * surface. The section now collapses and reopens in page state instead; a
 * `carryForwardTuckedAt` already on a lesson doc is ignored, never read.
 */
const useCarryForward = ({ studentId, refreshToken = 0 }) => {
	const [groups, setGroups] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	const load = useCallback(async () => {
		if (!studentId) {
			setGroups([]);
			return;
		}
		setIsLoading(true);
		setError('');
		try {
			const notes = await fetchCarryForwardNotes(studentId);
			// Normalise legacy vocabulary on read.
			const normalised = notes.map((n) => ({ ...n, status: mapLegacyStatus(n) }));
			setGroups(groupCarryForwardBySong(normalised));
		} catch (err) {
			console.error('Error loading carry-forward notes:', err);
			setError('Could not load previous notes.');
		} finally {
			setIsLoading(false);
		}
	}, [studentId]);

	useEffect(() => {
		load();
	}, [load, refreshToken]);

	const totalOpen = groups.reduce((sum, g) => sum + g.total, 0);

	return { groups, totalOpen, isLoading, error, reload: load };
};

export default useCarryForward;
